import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FiDownload, FiEdit3, FiX, FiCheck, FiPrinter,
    FiChevronRight, FiChevronLeft, FiSave, FiAlertCircle, FiUsers
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import api from '../../utils/api';

const formatDate = (dateStr) => {
    if (!dateStr || dateStr === '-') return '-';
    try {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
    } catch (e) { }
    return dateStr;
};

const CardFooter = () => (
    <div className="absolute bottom-0 left-0 right-0 h-[170px] overflow-hidden">

        <svg
            viewBox="80 0 300 170"
            preserveAspectRatio="none"
            className="absolute bottom-0 w-full h-full"
        >
            {/* 🔴 Red Base — LEFT SIDE raised */}
            <path
                d="
          M0,90
          C160,110 330,45 500,30
          L500,170
          L0,170
          Z
        "
                fill="#c62828"
            />

            {/* ⚫ Grey Swoosh — LEFT SIDE raised */}
            <path
                d="
          M0,35
          C100,140 370,35 500,28
          C430,10 190,130 60,90
          Z
        "
                fill="#5f5f5f"
            />
        </svg>

        {/* Footer Text */}
        <div
            className="absolute bottom-3 w-full px-4 text-center text-white flex flex-col items-center z-10"
            style={{
                fontSize: "8px",
                lineHeight: "1.4",
                fontWeight: "300",
                fontFamily: "Arial, sans-serif"
            }}
        >
            <p className="m-0">
                No. 47/4, Sathy Road West, North Zone, Saravanampatti, Coimbatore - 641 035
            </p>
            <p className="m-0">
                No. M18/E, Geethanjali Complex, 15th Avenue, Ashok Nagar, Chennai - 600 083
            </p>
            <p className="mt-1">
                Ph. No: +91 9500-777781, 93456 36682
            </p>
        </div>

    </div>
);

const IdCard = ({ employeeId, employeeIds = [], isOpen, onClose, onUpdateSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [employeeData, setEmployeeData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [errors, setErrors] = useState({});
    const [currentView, setCurrentView] = useState('front'); // 'front', 'back', or 'history'
    const [history, setHistory] = useState([]);

    // Bulk Mode States
    const isBulkMode = employeeIds?.length > 0;
    const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, status: 'idle' }); // idle, processing, done, validating
    const [missingDataList, setMissingDataList] = useState([]);
    const [showValidationConfirm, setShowValidationConfirm] = useState(false);

    const frontCardRef = useRef(null);
    const backCardRef = useRef(null);
    const captureFrontRef = useRef(null);
    const captureBackRef = useRef(null);

    // Template Paths
    const getBaseUrl = () => {
        const url = api.defaults.baseURL;
        return url.substring(0, url.lastIndexOf('/api'));
    };

    useEffect(() => {
        if (isOpen) {
            if (isBulkMode) {
                // Initialize Bulk Mode
                setEmployeeData(null); // Clear previous data
                setBulkProgress({ current: 0, total: employeeIds.length, status: 'idle' });
                setMissingDataList([]);
                setShowValidationConfirm(false);
            } else if (employeeId) {
                fetchEmployeeData(employeeId);
                fetchHistory(employeeId);
            }
        }
    }, [isOpen, employeeId, isBulkMode, employeeIds]);

    const validateAndGenerate = async () => {
        if (!employeeIds.length) return;

        setBulkProgress({ ...bulkProgress, status: 'validating' });
        const missingList = [];

        for (const id of employeeIds) {
            try {
                // Fetch data for validation without affecting main view state heavily if possible, 
                // but re-using api endpoint is easiest.
                const response = await api.get(`/employee/details?user_id=${id}`);
                if (response.data.status) {
                    const d = response.data.data;
                    const missing = [];
                    if (!d.aadhaar_name) missing.push('Name');
                    if (!d.designation) missing.push('Designation');
                    if (!d.dob) missing.push('DOB');
                    if (!d.blood_group) missing.push('Blood Group');
                    if (!d.mobile) missing.push('Mobile');

                    const emerg = d.family_details?.father?.contact || d.family_details?.mother?.contact || d.family_details?.emergency_contact;
                    if (!emerg) missing.push('Emergency Contact');

                    if (!d.address_communication) missing.push('Address');
                    if (!d.profile_image) missing.push('Photo');

                    if (missing.length > 0) {
                        missingList.push({
                            id: d.user_id,
                            name: d.aadhaar_name || `Employee ${d.user_id}`,
                            missing: missing
                        });
                    }
                }
            } catch (e) {
                console.error(e);
                missingList.push({ id, name: `ID: ${id}`, missing: ['Failed to fetch data'] });
            }
        }

        if (missingList.length > 0) {
            setMissingDataList(missingList);
            setShowValidationConfirm(true);
            setBulkProgress(prev => ({ ...prev, status: 'idle' }));
        } else {
            generateBulkPDF();
        }
    };

    const fetchHistory = async (id) => {
        try {
            const res = await api.get(`/id-card/history/${id}`);
            if (res.data.status) setHistory(res.data.data);
        } catch (error) {
            console.error('History fetch error:', error);
        }
    };

    const fetchEmployeeData = async (id) => {
        setLoading(true);
        try {
            const response = await api.get(`/employee/details?user_id=${id}`);
            if (response.data.status) {
                const data = response.data.data;
                setEmployeeData(data);

                // Initialize editable fields
                setEditData({
                    aadhaar_name: data.aadhaar_name || '',
                    designation: data.official_job_role || data.designation || '',
                    dob: data.dob || '',
                    blood_group: data.blood_group || '',
                    personal_email: data.personal_email || '',
                    mobile: data.mobile || '',
                    address_communication: data.address_communication || '',
                    emergency_contact: data.family_details?.father?.contact || data.family_details?.mother?.contact || ''
                });
                return data;
            } else {
                toast.error(response.data.message || 'Failed to fetch employee details');
                return null;
            }
        } catch (error) {
            console.error('Error fetching employee data:', error);
            toast.error('Server error while fetching data');
            return null;
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Name Validation
        if (!editData.aadhaar_name?.trim()) {
            newErrors.aadhaar_name = 'Name is required';
        } else if (editData.aadhaar_name.length > 50) {
            newErrors.aadhaar_name = 'Max 50 characters';
        }

        // Mobile Validation
        if (!editData.mobile) {
            newErrors.mobile = 'Mobile is required';
        }

        // Email Validation
        if (editData.personal_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editData.personal_email)) {
            newErrors.personal_email = 'Invalid email format';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            toast.error('Please fix validation errors');
            return;
        }

        setSaving(true);
        try {
            // Update local state only - do not call employee/details API
            const updatedLocalData = {
                ...employeeData,
                aadhaar_name: editData.aadhaar_name,
                designation: editData.designation,
                dob: editData.dob,
                blood_group: editData.blood_group,
                personal_email: editData.personal_email,
                mobile: editData.mobile,
                address_communication: editData.address_communication,
                family_details: {
                    ...employeeData.family_details,
                    emergency_contact: editData.emergency_contact
                }
            };

            setEmployeeData(updatedLocalData);
            setIsEditing(false);
            setErrors({});
            toast.success('Preview updated (Original record remains unchanged)');
        } catch (error) {
            console.error('Error updating preview:', error);
            toast.error('Failed to update preview');
        } finally {
            setSaving(false);
        }
    };

    const captureCard = async (element) => {
        return await html2canvas(element, {
            useCORS: true,
            scale: 3,
            backgroundColor: null,
            logging: false
        });
    };

    const prepareContainerForCapture = (container) => {
        // Force styles for capture
        container.style.transform = 'scale(1)';
        container.style.opacity = '1';
        container.style.position = 'absolute';
        container.style.top = '0';
        container.style.left = '0';
        container.style.zIndex = '-100';
    }

    const restoreContainer = (container) => {
        container.style.transform = '';
        container.style.opacity = '';
        container.style.position = '';
        container.style.top = '';
        container.style.left = '';
        container.style.zIndex = '';
    }

    const generateSinglePDF = async () => {
        await generatePDF([employeeId]);
    };

    const generateBulkPDF = async () => {
        await generatePDF(employeeIds);
    };

    const getDataUrl = (url) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = reject;
            img.src = url;
        });
    };

    const generatePDF = async (idsToProcess) => {
        if (!idsToProcess || idsToProcess.length === 0) return;

        const toastId = toast.loading(isBulkMode ? 'Initializing Bulk Generation...' : 'Generating PDF...');
        setBulkProgress({ current: 0, total: idsToProcess.length, status: 'processing' });

        // Fetch Logo
        let logoData = null;
        try {
            logoData = await getDataUrl(`${getBaseUrl()}/uploads/banner/logo.png`);
        } catch (e) {
            console.error("Failed to load logo for PDF", e);
        }

        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const cardWidth = 56;
        const cardHeight = 88;
        const xMargin = 20; // Side margins
        const topMargin = 50; // Increased for Header
        const bottomMargin = 20;

        const itemsPerPage = 2;
        const totalPages = Math.ceil(idsToProcess.length / itemsPerPage);
        const generationDate = new Date().toLocaleString();

        const drawHeaderFooter = (pageNo) => {
            const pageWidth = pdf.internal.pageSize.width;
            const pageHeight = pdf.internal.pageSize.height;

            // Header
            if (logoData) {
                const logoW = 60;
                const logoX = (pageWidth - logoW) / 2;
                pdf.addImage(logoData, 'PNG', logoX, 5, logoW, 0);
            }

            pdf.setFontSize(14);
            pdf.setFont("helvetica", "bold");
            pdf.text("ID Card Generation Report", pageWidth / 2, 35, { align: 'center' });

            pdf.setDrawColor(200, 200, 200);
            pdf.line(10, 40, pageWidth - 10, 40);

            // Footer
            pdf.setFontSize(8);
            pdf.setFont("helvetica", "italic");
            pdf.text(`Generated on: ${generationDate}`, 10, pageHeight - 10);
            pdf.text(`Page ${pageNo}/${totalPages}`, pageWidth - 10, pageHeight - 10, { align: 'right' });
        };

        const versionsToSave = [];

        try {
            // Draw Header for Page 1
            drawHeaderFooter(1);

            for (let i = 0; i < idsToProcess.length; i++) {
                const currentId = idsToProcess[i];

                // Update Progress UI
                setBulkProgress({ current: i + 1, total: idsToProcess.length, status: 'processing' });
                if (isBulkMode) toast.loading(`Processing ${i + 1}/${idsToProcess.length}...`, { id: toastId });

                // 1. Get Data - Use existing local data if in single mode to preserve current edits
                const data = (!isBulkMode && currentId === employeeId && employeeData)
                    ? employeeData
                    : await fetchEmployeeData(currentId);
                
                if (!data) continue;

                // 2. Wait for React to render
                await new Promise(resolve => setTimeout(resolve, 500));

                // 3. Prepare DOM for Capture
                if (!captureFrontRef.current || !captureBackRef.current) {
                    console.warn(`Capture refs not ready for ID ${currentId}`);
                    continue;
                }

                const frontCanvas = await captureCard(captureFrontRef.current);
                const backCanvas = await captureCard(captureBackRef.current);

                const frontImgData = frontCanvas.toDataURL('image/png');
                const backImgData = backCanvas.toDataURL('image/png');

                // 5. Add to PDF
                const indexOnPage = i % itemsPerPage;

                // Add new page if needed
                if (i > 0 && indexOnPage === 0) {
                    pdf.addPage();
                    drawHeaderFooter((i / itemsPerPage) + 1);
                }

                // Layout Config
                const verticalGap = 15;
                const textSpace = 10;
                const rowHeight = cardHeight + verticalGap + textSpace; // ~113mm

                // Y Position
                const yOffset = topMargin + (indexOnPage * rowHeight);

                // Add Name Title
                pdf.setFontSize(10);
                pdf.setFont("helvetica", "bold");
                const nameTitle = data.aadhaar_name ? data.aadhaar_name : 'EMPLOYEE';
                // Reset color to black (footer might have set gray)
                pdf.setTextColor(0, 0, 0);
                pdf.text(`#${i + 1} - ${nameTitle}`, xMargin, yOffset + 5);

                const cardY = yOffset + 8;

                // Add Front & Back
                pdf.addImage(frontImgData, 'PNG', xMargin, cardY, cardWidth, cardHeight);
                pdf.setLineWidth(0.1);
                pdf.setDrawColor(200, 200, 200);
                pdf.rect(xMargin, cardY, cardWidth, cardHeight); // Border Front

                pdf.addImage(backImgData, 'PNG', xMargin + cardWidth + 10, cardY, cardWidth, cardHeight);
                pdf.rect(xMargin + cardWidth + 10, cardY, cardWidth, cardHeight); // Border Back

                // Collect version data
                versionsToSave.push({
                    user_id: currentId,
                    card_data: { ...data, generated_at: new Date().toISOString() }
                });
            }

            // Download
            const fileName = isBulkMode ? `Bulk_ID_Cards_${new Date().toISOString().slice(0, 10)}.pdf` : `${employeeData?.aadhaar_name || 'Employee'}_ID_Card.pdf`;
            pdf.save(fileName);

            // Batch Save History
            if (versionsToSave.length > 0) {
                try {
                    await api.post('/id-card/save-bulk-versions', { versions: versionsToSave });
                } catch (e) { console.error("Failed to save bulk history", e); }
            }

            toast.success('ID Cards downloaded successfully', { id: toastId });
            setBulkProgress(prev => ({ ...prev, status: 'done' }));

            if (isBulkMode) {
                setTimeout(onClose, 2000);
            }

        } catch (error) {
            console.error('PDF Generation Error:', error);
            toast.error('Failed to generate PDF', { id: toastId });
            setBulkProgress(prev => ({ ...prev, status: 'idle' }));
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md overflow-hidden">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    className="bg-white rounded-2xl lg:rounded-3xl shadow-2xl w-full max-w-5xl h-auto max-h-[85vh] flex flex-col overflow-hidden"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                        <div className="flex items-center gap-3 lg:gap-4">
                            <div className="p-2 lg:p-3 bg-red-100 text-red-600 rounded-xl lg:rounded-2xl">
                                <FiPrinter size={20} className="lg:w-6 lg:h-6" />
                            </div>
                            <div>
                                <h2 className="text-lg lg:text-2xl font-bold text-gray-800">
                                    {isBulkMode ? 'Bulk ID Card Generation' : 'Generate ID Card'}
                                </h2>
                                <p className="text-xs lg:text-sm text-gray-500 font-medium">
                                    {isBulkMode ? `Generating cards for ${employeeIds.length} employees` : 'Preview and edit details'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 overflow-y-auto p-2 lg:p-4 bg-gray-50/30">
                        {isBulkMode ? (
                            <div className="flex flex-col items-center justify-center h-full gap-8">
                                {

                                    bulkProgress.status === 'processing' || bulkProgress.status === 'validating' ? (
                                        <div className="w-full max-w-md space-y-6 flex flex-col items-center">
                                            {/* Animated Card Stack Visual */}
                                            <div className="relative w-32 h-44">
                                                <motion.div className="absolute inset-0 bg-white border border-gray-200 rounded-xl shadow-sm" animate={{ rotate: 10, scale: 0.9 }} />
                                                <motion.div className="absolute inset-0 bg-white border border-gray-200 rounded-xl shadow-md" animate={{ rotate: -5, scale: 0.95 }} />
                                                <motion.div
                                                    className="absolute inset-0 bg-white border border-red-100 rounded-xl shadow-lg flex items-center justify-center border-t-4 border-t-red-600"
                                                    animate={{ y: [0, -10, 0] }}
                                                    transition={{ repeat: Infinity, duration: 1.5 }}
                                                >
                                                    <div className="flex flex-col items-center gap-2">
                                                        <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" />
                                                        <div className="w-16 h-2 bg-gray-100 rounded animate-pulse" />
                                                        <div className="w-10 h-2 bg-gray-100 rounded animate-pulse" />
                                                    </div>
                                                </motion.div>
                                            </div>

                                            <div className="w-full space-y-2 text-center">
                                                <h4 className="text-lg font-bold text-gray-800">
                                                    {bulkProgress.status === 'validating' ? 'Validating Data...' : 'Generating ID Cards...'}
                                                </h4>
                                                {bulkProgress.status === 'processing' && (
                                                    <>
                                                        <div className="flex justify-between text-sm font-medium text-gray-500 px-1">
                                                            <span>Processing</span>
                                                            <span>{Math.round((bulkProgress.current / bulkProgress.total) * 100)}%</span>
                                                        </div>
                                                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                                            <motion.div
                                                                className="h-full bg-red-600"
                                                                initial={{ width: 0 }}
                                                                animate={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                                                                transition={{ duration: 0.5 }}
                                                            />
                                                        </div>
                                                        <p className="text-xs text-gray-400">Card {bulkProgress.current} of {bulkProgress.total}</p>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ) : showValidationConfirm ? (
                                        <div className="w-full max-w-2xl flex flex-col h-full bg-white rounded-2xl shadow-sm border border-red-100 overflow-hidden">
                                            <div className="p-6 bg-red-50/50 border-b border-red-100 flex items-center gap-4">
                                                <div className="bg-red-100 p-3 rounded-full text-red-600">
                                                    <FiAlertCircle size={24} />
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-bold text-gray-900">Missing Data Detected</h3>
                                                    <p className="text-sm text-gray-600">The following employees have incomplete details required for the ID card.</p>
                                                </div>
                                            </div>
                                            <div className="flex-1 overflow-y-auto p-2">
                                                <table className="w-full text-left text-sm">
                                                    <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-xs">
                                                        <tr>
                                                            <th className="px-4 py-3">ID</th>
                                                            <th className="px-4 py-3">Name</th>
                                                            <th className="px-4 py-3 text-red-600">Missing Fields</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {missingDataList.map((item, idx) => (
                                                            <tr key={idx} className="hover:bg-gray-50">
                                                                <td className="px-4 py-3 font-mono text-xs">{item.id}</td>
                                                                <td className="px-4 py-3 font-medium">{item.name}</td>
                                                                <td className="px-4 py-3 text-red-600 font-medium">
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {item.missing.map((f, i) => (
                                                                            <span key={i} className="bg-red-50 border border-red-100 px-1.5 py-0.5 rounded text-[10px]">{f}</span>
                                                                        ))}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                                                <button
                                                    onClick={() => { setShowValidationConfirm(false); setMissingDataList([]); }}
                                                    className="px-4 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={generateBulkPDF}
                                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg shadow-sm"
                                                >
                                                    Continue Anyway
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center h-full gap-8 animate-in fade-in zoom-in duration-300">
                                            <div className="text-center space-y-4">
                                                <div className="relative inline-block">
                                                    <div className="absolute -inset-1 bg-red-100 rounded-full blur-lg opacity-50"></div>
                                                    <div className="relative bg-white p-6 rounded-2xl shadow-xl border border-red-50">
                                                        <div className="flex items-center justify-center gap-[-10px]">
                                                            <div className="w-16 h-20 bg-gray-50 rounded border border-gray-200 transform -rotate-12 shadow-sm origin-bottom-right" />
                                                            <div className="w-16 h-20 bg-gray-50 rounded border border-gray-200 transform rotate-12 shadow-sm origin-bottom-left -ml-8" />
                                                            <div className="w-16 h-20 bg-white rounded border-2 border-red-500 z-10 -ml-8 flex items-center justify-center shadow-md">
                                                                <FiUsers className="w-6 h-6 text-red-500" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <h3 className="text-2xl font-bold text-gray-800 mt-4">Generate {employeeIds.length} ID Cards</h3>
                                                <p className="text-gray-500 max-w-md mx-auto">
                                                    Ready to generate a PDF containing ID cards for all selected employees.
                                                    <br />
                                                    <span className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded mt-2 inline-block">2 Cards Per Page</span>
                                                </p>
                                            </div>

                                            <div className="flex gap-4">
                                                <button
                                                    onClick={onClose}
                                                    className="px-6 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold rounded-xl transition-all"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={validateAndGenerate}
                                                    className="px-8 py-2.5 bg-gray-900 hover:bg-black text-white font-bold rounded-xl shadow-lg ring-4 ring-gray-100 flex items-center gap-2 transition-all transform hover:scale-105"
                                                >
                                                    <FiDownload />
                                                    Download
                                                </button>
                                            </div>
                                        </div>
                                    )
                                }

                            </div>
                        ) : loading ? (
                            <div className="h-full flex flex-col items-center justify-center gap-4 py-20">
                                <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                <p className="text-gray-500 font-bold animate-pulse">Fetching Employee Data...</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-6 lg:h-full">

                                {/* Left Side: Preview */}
                                <div className="lg:col-span-7 flex flex-col">
                                    <div className="flex items-center justify-between mb-4 lg:mb-6">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setCurrentView('front')}
                                                className={`px-4 py-2 lg:px-6 lg:py-2 rounded-xl text-xs lg:text-sm font-bold transition-all ${currentView === 'front' ? 'bg-red-600 text-white shadow-lg shadow-red-100' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'}`}
                                            >
                                                Front
                                            </button>
                                            <button
                                                onClick={() => setCurrentView('back')}
                                                className={`px-4 py-2 lg:px-6 lg:py-2 rounded-xl text-xs lg:text-sm font-bold transition-all ${currentView === 'back' ? 'bg-red-600 text-white shadow-lg shadow-red-100' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'}`}
                                            >
                                                Back
                                            </button>
                                        </div>
                                        <div className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest hidden sm:block">
                                            Scale: 1:1 Preview
                                        </div>
                                    </div>

                                    <div className="flex-1 flex items-center justify-center bg-gray-100/50 rounded-3xl border border-dashed border-gray-300 relative overflow-hidden p-2 lg:p-4 min-h-0 lg:min-h-[310px]">

                                        {/* Card Container - Front */}
                                        <div className={`transition-all duration-500 origin-top transform sm:scale-75 scale-[0.55] -mb-[245px] sm:-mb-[135px] ${currentView === 'front' ? 'opacity-100' : 'opacity-0 pointer-events-none absolute'}`} style={{ fontFamily: 'Arial, sans-serif' }}>
                                            <div
                                                ref={frontCardRef}
                                                className="w-[350px] h-[550px] bg-white rounded-[20px] shadow-2xl relative overflow-hidden flex flex-col items-center"
                                            >
                                                {/* Front Header Section */}
                                                <div className="w-full flex flex-col items-center pt-8">
                                                    {/* Logo Block */}
                                                    <div className="flex flex-col items-center mb-1">
                                                        <div className="w-64 mb-1">
                                                            <img src={`${getBaseUrl()}/uploads/banner/logo.png`} crossOrigin="anonymous" alt="eNova Logo" className="w-full object-contain" />
                                                        </div>
                                                        {/* <span className="text-[8px] font-bold text-gray-800 tracking-tight">- Your Technology Solutions Partner -</span> */}
                                                    </div>
                                                    {/* Card Title */}
                                                    <h2 className="text-[#c62828] font-bold text-[15px] uppercase mt-2">
                                                        EMPLOYEE IDENTITY CARD
                                                    </h2>
                                                </div>

                                                {/* Photo Frame */}
                                                <div className="mt-3 p-1 bg-white shadow-lg border border-gray-50">
                                                    <div className="w-[145px] h-[175px] bg-gray-50 overflow-hidden border border-gray-400 flex items-center justify-center">
                                                        {employeeData?.profile_image ? (
                                                            <img src={employeeData.profile_image} crossOrigin="anonymous" className="w-full h-full object-cover" alt="Profile" />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-gray-300 font-bold text-[10px] uppercase">Photo</div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Name & Designation Block */}
                                                <div className="mt-3 text-center px-4 w-full z-10 flex flex-col gap-1">
                                                    <h3 className="font-bold text-gray-900 leading-tight text-[17px]">
                                                        {editData.aadhaar_name || 'NAME'}
                                                    </h3>
                                                    <p className="font-bold text-[#000] text-[10px]">
                                                        {editData.designation || 'DESIGNATION'}
                                                    </p>
                                                </div>

                                                {/* Waves */}
                                                <CardFooter />
                                            </div>
                                        </div>

                                        {/* Card Container - Back */}
                                        <div className={`transition-all duration-500 origin-top transform sm:scale-75 scale-[0.55] -mb-[245px] sm:-mb-[135px] ${currentView === 'back' ? 'opacity-100' : 'opacity-0 pointer-events-none absolute'}`} style={{ fontFamily: 'Arial, sans-serif' }}>
                                            <div
                                                ref={backCardRef}
                                                className="w-[350px] h-[550px] bg-white rounded-[20px] shadow-2xl relative overflow-hidden"
                                            >
                                                {/* Back Main Content Container */}
                                                <div className="absolute top-[40px] left-[30px] right-[30px] bottom-[200px] z-10 flex flex-col" style={{ fontFamily: 'Arial, sans-serif' }}>

                                                    {/* Section Header */}
                                                    <div className="mb-3">
                                                        <h4 className="text-[#c62828] font-bold text-[13px] uppercase">
                                                            PERSONAL DETAILS
                                                        </h4>
                                                    </div>

                                                    {/* Exact Grid Layout from Template - Tightened spacing */}
                                                    <div className="grid grid-cols-[105px_10px_1fr] gap-y-[15px] text-[9.5px] text-black w-full items-baseline leading-[1.2]">
                                                        {/* Labels match back template alignment */}
                                                        <span className="text-gray-700 font-bold">Name</span>
                                                        <span className="text-center font-bold">:</span>
                                                        <span className="text-black font-normal">{editData.aadhaar_name || '-'}</span>

                                                        <span className="text-gray-700 font-bold">D O B</span>
                                                        <span className="text-center font-bold">:</span>
                                                        <span className="text-black font-normal">{formatDate(editData.dob) || '-'}</span>

                                                        <span className="text-gray-700 font-bold">Blood Group</span>
                                                        <span className="text-center font-bold">:</span>
                                                        <span className="text-black font-normal">{editData.blood_group || '-'}</span>

                                                        <span className="text-gray-700 font-bold">Mobile No</span>
                                                        <span className="text-center font-bold">:</span>
                                                        <span className="text-black font-normal">{editData.mobile || '-'}</span>

                                                        <div className="text-gray-700 font-bold leading-tight">
                                                            Emergency<br />Phone No.
                                                        </div>
                                                        <span className="text-center font-bold">:</span>
                                                        <span className="text-black font-normal">{editData.emergency_contact || '-'}</span>

                                                        <span className="text-gray-700 font-bold">E-mail Id</span>
                                                        <span className="text-center font-bold">:</span>
                                                        <span className="break-all text-black font-normal">{employeeData?.personal_email || '-'}</span>

                                                        <span className="text-gray-700 font-bold">Address</span>
                                                        <span className="text-center font-bold">:</span>
                                                        <div className="leading-[1.2] whitespace-pre-wrap text-black font-normal">{editData.address_communication || '-'}</div>
                                                    </div>
                                                </div>

                                                {/* Signature & Logo Block (Middle-Bottom Right) */}
                                                <div className="absolute top-[320px] right-[25px] flex flex-col items-center z-10" style={{ fontFamily: 'Arial, sans-serif' }}>
                                                    {/* Signature Line Area */}
                                                    <div className="w-[130px] h-15 mb-1 flex items-end justify-center">
                                                        <img src={`${getBaseUrl()}/uploads/banner/sign.png`} crossOrigin="anonymous" alt="Sign" className="h-full object-contain" />
                                                    </div>
                                                    <div className="w-full h-[0.5px] bg-black"></div>
                                                    <span className="text-[9px] font-bold text-gray-900 mt-0.5">Managing Director</span>

                                                    {/* Small EnoVA Logo below Signature - Tagline Removed */}
                                                    <div className="mt-2 flex flex-col items-center">
                                                        <div className="w-14">
                                                            <img src={`${getBaseUrl()}/uploads/banner/logo.png`} crossOrigin="anonymous" alt="eNova Logo" className="w-full object-contain" />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Footer Waves */}
                                                <CardFooter />
                                            </div>
                                        </div>
                                    </div>

                                </div>

                                {/* Right Side: Controls & Edit */}
                                <div className="lg:col-span-5 lg:h-full flex flex-col bg-white rounded-3xl shadow-sm border border-gray-100 lg:overflow-hidden h-auto">
                                    <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                                        <h3 className="font-bold text-gray-800 flex items-center gap-2">
                                            <FiEdit3 className="text-red-500" />
                                            ID Details
                                        </h3>
                                        <button
                                            onClick={() => {
                                                if (isEditing) {
                                                    // Revert changes when canceling
                                                    setEditData({
                                                        aadhaar_name: employeeData.aadhaar_name || '',
                                                        designation: employeeData.official_job_role || employeeData.designation || '',
                                                        dob: employeeData.dob || '',
                                                        blood_group: employeeData.blood_group || '',
                                                        personal_email: employeeData.personal_email || '',
                                                        mobile: employeeData.mobile || '',
                                                        address_communication: employeeData.address_communication || '',
                                                        emergency_contact: employeeData.family_details?.father?.contact || employeeData.family_details?.mother?.contact || employeeData.family_details?.emergency_contact || ''
                                                    });
                                                    setErrors({});
                                                }
                                                setIsEditing(!isEditing);
                                            }}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2 ${isEditing ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                        >
                                            {isEditing ? <><FiX /> Cancel</> : <><FiEdit3 /> Edit</>}
                                        </button>
                                    </div>

                                    <div className="overflow-y-auto p-4 space-y-3">
                                        <div className="bg-blue-50 p-3 rounded-xl border border-blue-100 flex gap-3">
                                            <FiAlertCircle className="text-blue-500 flex-shrink-0 mt-0.5" />
                                            <p className="text-[10px] text-blue-700 leading-relaxed">
                                                Edit details here to update the ID card preview.
                                                These changes are temporary for this session and will <strong>NOT</strong> update the original employee form or database.
                                            </p>
                                        </div>

                                        <div className="space-y-3">
                                            <div className="grid grid-cols-2 gap-3">
                                                {/* Name */}
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-gray-500 uppercase">Name</label>
                                                    {isEditing ? (
                                                        <input
                                                            type="text"
                                                            value={editData.aadhaar_name}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                if (/^[a-zA-Z\s]*$/.test(val) && val.length <= 50) {
                                                                    setEditData({ ...editData, aadhaar_name: val });
                                                                }
                                                            }}
                                                            className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                                                            placeholder="Employee Name"
                                                        />
                                                    ) : (
                                                        <div className="w-full p-2 bg-gray-50 border border-gray-100 rounded-lg text-xs font-bold text-gray-700 truncate">
                                                            {editData.aadhaar_name || '-'}
                                                        </div>
                                                    )}
                                                    {errors.aadhaar_name && <p className="text-red-500 text-[9px] font-bold">{errors.aadhaar_name}</p>}
                                                </div>

                                                {/* Designation */}
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-gray-500 uppercase">Designation</label>
                                                    {isEditing ? (
                                                        <input
                                                            type="text"
                                                            value={editData.designation}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                if (/^[a-zA-Z\s.,&-]*$/.test(val) && val.length <= 50) {
                                                                    setEditData({ ...editData, designation: val });
                                                                }
                                                            }}
                                                            className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                                                            placeholder="Designation"
                                                        />
                                                    ) : (
                                                        <div className="w-full p-2 bg-gray-50 border border-gray-100 rounded-lg text-xs font-bold text-gray-700 truncate">
                                                            {editData.designation || '-'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-3">
                                                {/* DOB */}
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-gray-500 uppercase">DOB</label>
                                                    {isEditing ? (
                                                        <input
                                                            type="date"
                                                            value={editData.dob}
                                                            onChange={(e) => setEditData({ ...editData, dob: e.target.value })}
                                                            className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                                                        />
                                                    ) : (
                                                        <div className="w-full p-2 bg-gray-50 border border-gray-100 rounded-lg text-xs font-bold text-gray-700">
                                                            {editData.dob || '-'}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Blood Group */}
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-gray-500 uppercase">Blood Group</label>
                                                    {isEditing ? (
                                                        <input
                                                            type="text"
                                                            value={editData.blood_group}
                                                            onChange={(e) => setEditData({ ...editData, blood_group: e.target.value })}
                                                            className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                                                            placeholder="e.g. O+"
                                                        />
                                                    ) : (
                                                        <div className="w-full p-2 bg-gray-50 border border-gray-100 rounded-lg text-xs font-bold text-gray-700">
                                                            {editData.blood_group || '-'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-3">
                                                {/* Mobile */}
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-gray-500 uppercase">Mobile</label>
                                                    {isEditing ? (
                                                        <input
                                                            type="tel"
                                                            value={editData.mobile}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                if (/^[0-9+\s-]*$/.test(val) && val.length <= 20) {
                                                                    setEditData({ ...editData, mobile: val });
                                                                }
                                                            }}
                                                            className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                                                            placeholder="Mobile No"
                                                        />
                                                    ) : (
                                                        <div className="w-full p-2 bg-gray-50 border border-gray-100 rounded-lg text-xs font-bold text-gray-700">
                                                            {editData.mobile || '-'}
                                                        </div>
                                                    )}
                                                    {errors.mobile && <p className="text-red-500 text-[9px] font-bold">{errors.mobile}</p>}
                                                </div>

                                                {/* Emergency Contact */}
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-gray-500 uppercase">Emergency</label>
                                                    {isEditing ? (
                                                        <input
                                                            type="tel"
                                                            value={editData.emergency_contact}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                if (/^[0-9+\s-]*$/.test(val) && val.length <= 20) {
                                                                    setEditData({ ...editData, emergency_contact: val });
                                                                }
                                                            }}
                                                            className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                                                            placeholder="Emergency No"
                                                        />
                                                    ) : (
                                                        <div className="w-full p-2 bg-gray-50 border border-gray-100 rounded-lg text-xs font-bold text-gray-700">
                                                            {editData.emergency_contact || '-'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Email */}
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-gray-500 uppercase">Email</label>
                                                {isEditing ? (
                                                    <input
                                                        type="email"
                                                        value={editData.personal_email}
                                                        onChange={(e) => {
                                                            setEditData({ ...editData, personal_email: e.target.value });
                                                            if (errors.personal_email) setErrors({ ...errors, personal_email: null });
                                                        }}
                                                        className={`w-full p-2 bg-gray-50 border rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all ${errors.personal_email ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                                                        placeholder="Email Address"
                                                    />
                                                ) : (
                                                    <div className="w-full p-2 bg-gray-50 border border-gray-100 rounded-lg text-xs font-bold text-gray-700 truncate">
                                                        {editData.personal_email || '-'}
                                                    </div>
                                                )}
                                                {errors.personal_email && <p className="text-red-500 text-[9px] font-bold">{errors.personal_email}</p>}
                                            </div>

                                            {/* Address */}
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-gray-500 uppercase">Address</label>
                                                {isEditing ? (
                                                    <textarea
                                                        value={editData.address_communication}
                                                        onChange={(e) => setEditData({ ...editData, address_communication: e.target.value })}
                                                        rows={2}
                                                        className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all resize-none"
                                                        placeholder="Enter full address"
                                                    />
                                                ) : (
                                                    <div className="w-full p-2 bg-gray-50 border border-gray-100 rounded-lg text-xs font-bold text-gray-700 min-h-[50px]">
                                                        {editData.address_communication || '-'}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="p-4 border-t border-gray-100 bg-gray-50/50 flex flex-col gap-2">
                                        <AnimatePresence>
                                            {isEditing && (
                                                <motion.button
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: 10 }}
                                                    onClick={handleSave}
                                                    disabled={saving}
                                                    className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold shadow-lg shadow-red-200 flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                >
                                                    {saving ? (
                                                        <>
                                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                            Saving...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiCheck size={16} />
                                                            Update Preview
                                                        </>
                                                    )}
                                                </motion.button>
                                            )}
                                        </AnimatePresence>

                                        <button
                                            onClick={generateSinglePDF}
                                            className="w-full py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl font-bold shadow-lg shadow-gray-200 flex items-center justify-center gap-2 transition-all group text-sm"
                                        >
                                            <FiDownload size={16} className="group-hover:-translate-y-1 transition-transform" />
                                            Download ID Card (PDF)
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {/* Hidden container for PDF Capture (Always Rendered, Off Screen) */}
                    <div className="absolute opacity-0 pointer-events-none -z-50 top-0 left-0">
                        {employeeData && (
                            <div className="flex">
                                <div ref={captureFrontRef} className="w-[350px] h-[550px] bg-white relative overflow-hidden flex flex-col items-center">
                                    {/* Re-use Header */}
                                    <div className="w-full flex flex-col items-center pt-8">
                                        <div className="flex flex-col items-center mb-1">
                                            <div className="w-64 mb-1">
                                                <img src={`${getBaseUrl()}/uploads/banner/logo.png`} crossOrigin="anonymous" alt="eNova Logo" className="w-full object-contain" />
                                            </div>
                                        </div>
                                        <h2 className="text-[#c62828] font-bold text-[15px] uppercase mt-2">EMPLOYEE IDENTITY CARD</h2>
                                    </div>
                                    <div className="mt-3 p-1 bg-white shadow-lg border border-gray-50">
                                        <div className="w-[145px] h-[175px] bg-gray-50 overflow-hidden border border-gray-400 flex items-center justify-center">
                                            {employeeData?.profile_image ? (
                                                <img src={employeeData.profile_image} crossOrigin="anonymous" className="w-full h-full object-cover" alt="Profile" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-gray-300 font-bold text-[10px] uppercase">Photo</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-3 text-center px-4 w-full z-10 flex flex-col gap-1">
                                        <h3 className="font-bold text-gray-900 leading-tight text-[17px]">{editData.aadhaar_name || 'NAME'}</h3>
                                        <p className="font-bold text-[#000] text-[10px]">{editData.designation || 'DESIGNATION'}</p>
                                    </div>
                                    <CardFooter />
                                </div>

                                <div ref={captureBackRef} className="w-[350px] h-[550px] bg-white relative overflow-hidden">
                                    <div className="absolute top-[40px] left-[30px] right-[30px] bottom-[200px] z-10 flex flex-col" style={{ fontFamily: 'Arial, sans-serif' }}>
                                        <div className="mb-3"><h4 className="text-[#c62828] font-bold text-[13px] uppercase">PERSONAL DETAILS</h4></div>
                                        <div className="grid grid-cols-[105px_10px_1fr] gap-y-[15px] text-[9.5px] text-black w-full items-baseline leading-[1.2]">
                                            <span className="text-gray-700 font-bold">Name</span><span className="text-center font-bold">:</span><span className="text-black font-normal">{editData.aadhaar_name || '-'}</span>
                                            <span className="text-gray-700 font-bold">D O B</span><span className="text-center font-bold">:</span><span className="text-black font-normal">{formatDate(editData.dob) || '-'}</span>
                                            <span className="text-gray-700 font-bold">Blood Group</span><span className="text-center font-bold">:</span><span className="text-black font-normal">{editData.blood_group || '-'}</span>
                                            <span className="text-gray-700 font-bold">Mobile No</span><span className="text-center font-bold">:</span><span className="text-black font-normal">{editData.mobile || '-'}</span>
                                            <div className="text-gray-700 font-bold leading-tight">Emergency<br />Phone No.</div><span className="text-center font-bold">:</span><span className="text-black font-normal">{editData.emergency_contact || '-'}</span>
                                            <span className="text-gray-700 font-bold">E-mail Id</span><span className="text-center font-bold">:</span><span className="break-all text-black font-normal">{employeeData?.personal_email || '-'}</span>
                                            <span className="text-gray-700 font-bold">Address</span><span className="text-center font-bold">:</span><div className="leading-[1.2] whitespace-pre-wrap text-black font-normal">{editData.address_communication || '-'}</div>
                                        </div>
                                    </div>
                                    <div className="absolute top-[320px] right-[25px] flex flex-col items-center z-10" style={{ fontFamily: 'Arial, sans-serif' }}>
                                        <div className="w-[130px] h-15 mb-1 flex items-end justify-center"><img src={`${getBaseUrl()}/uploads/banner/sign.png`} crossOrigin="anonymous" alt="Sign" className="h-full object-contain" /></div>
                                        <div className="w-full h-[0.5px] bg-black"></div>
                                        <span className="text-[9px] font-bold text-gray-900 mt-0.5">Managing Director</span>
                                        <div className="mt-2 flex flex-col items-center"><div className="w-14"><img src={`${getBaseUrl()}/uploads/banner/logo.png`} crossOrigin="anonymous" alt="eNova Logo" className="w-full object-contain" /></div></div>
                                    </div>
                                    <CardFooter />
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div >
            </div >
        </AnimatePresence >
    );
};

export default IdCard;
